import { forwardRef, useMemo } from "react";
import { clsx } from "clsx";

import { Icon } from "../icon/Icon";

import type { CSSProperties } from "react";
import type { IconProps } from "../icon/Icon";

export interface AudioControlProps {
  isActive?: boolean;
  appearance?: "primary" | "secondary" | "tertiary";
  title?: string;
  type:
    | "play-pause"
    | "replay"
    | "go-backward"
    | "go-forward"
    | "closed-captioning"
    | "expand"
    | "airplay";
  onClick?: () => void;
}

export function getIconProps({
  isActive,
  type,
}: Partial<AudioControlProps>): IconProps & { style?: CSSProperties } {
  switch (type) {
    case "closed-captioning":
      return {
        icon: "closed-captioning",
        variant: isActive ? "solid" : "regular",
      };
    case "airplay":
      return {
        icon: "airplay",
        variant: isActive ? "solid" : "regular",
      };
    case "go-backward":
      return {
        icon: "go-backward-15",
      };
    case "go-forward":
      return {
        icon: "go-forward-30",
      };
    case "expand":
      return {
        icon: isActive ? "angle-up" : "angle-down",
      };
    case "play-pause":
      return {
        icon: isActive ? "play" : "pause",
        variant: "solid",
        style: isActive ? { left: "2px" } : {},
      };
    case "replay":
      return {
        icon: "arrow-rotate-right",
        variant: "regular",
        size: "var(--size-6)",
      };
    default:
      return { icon: "circle-question" };
  }
}

export const AudioControl = forwardRef<HTMLButtonElement, AudioControlProps>(
  function AudioControlImpl(
    { appearance, isActive, title, type, onClick },
    ref,
  ) {
    const className = clsx("AudioControl", {
      [`AudioControl--${appearance}`]: Boolean(appearance),
    });

    const iconProps = useMemo(
      () => getIconProps({ appearance, isActive, type }),
      [appearance, isActive, type],
    );

    return (
      <button
        ref={ref}
        aria-label={title}
        aria-pressed={isActive}
        className={className}
        title={title}
        onClick={onClick}
      >
        <span style={iconProps.style}>
          <Icon icon={iconProps.icon} variant={iconProps.variant} />
        </span>
      </button>
    );
  },
);
